'use client';

// Зачем: гость P2P накладывает снимок NPC от хоста на локальный массив без полной замены состояния ИИ.

import type {
  MysteryRoomDoorRuntimeState,
  MysteryRoomHazardRuntimeState,
  MysteryRoomLightRuntimeState,
  MysteryRoomNpcState,
  MysteryRoomTriggerRuntimeState,
  MysteryRoomWorldAlarmState,
} from '../core/localRuntime';
import type {
  MrP2PDoorSnapshot,
  MrP2PHazardSnapshot,
  MrP2PLightSnapshot,
  MrP2PNpcSnapshot,
  MrP2PTriggerSnapshot,
  MrP2PWorldAlarmSnapshot,
} from './p2pTransport';
import type { MysteryRoomLocalBotState } from '../core/localRuntime';

/** Зачем: плавное положение между сетевыми тиками (у гостя ~100ms). */
export function mergeExternalRemotePeerBots(
  prevBots: MysteryRoomLocalBotState[] | undefined,
  incoming: MysteryRoomLocalBotState[],
): MysteryRoomLocalBotState[] {
  const prevById = new Map((prevBots ?? []).filter((b) => b.remotePeer).map((b) => [b.id, b]));
  return incoming.map((bot) => {
    const nwTarget = { ...bot.position };
    const old = prevById.get(bot.id);
    if (!old) {
      return { ...bot, target: nwTarget, position: { ...nwTarget } };
    }
    const jump = Math.hypot(nwTarget.x - old.target.x, nwTarget.y - old.target.y);
    const snap = jump > 150;
    return {
      ...bot,
      target: nwTarget,
      position: snap ? { ...nwTarget } : { ...old.position },
    };
  });
}

export function mergeMrP2pNpcSnapshots(base: MysteryRoomNpcState[], snaps: MrP2PNpcSnapshot[]): MysteryRoomNpcState[] {
  if (!snaps.length) return base;
  const byId = new Map(snaps.map((s) => [s.id, s]));
  return base.map((n) => {
    const s = byId.get(n.id);
    if (!s) return n;
    return {
      ...n,
      position: { ...s.position },
      velocity: { ...s.velocity },
      facing: s.facing,
      animationState: s.animationState,
      alive: s.alive,
      zoneId: s.zoneId ?? n.zoneId,
    };
  });
}

export function mergeMrP2pLightSnapshots(base: MysteryRoomLightRuntimeState[], snaps: MrP2PLightSnapshot[]): MysteryRoomLightRuntimeState[] {
  if (!snaps.length) return base;
  const byId = new Map(snaps.map((s) => [s.id, s.enabled]));
  return base.map((light) => {
    const en = byId.get(light.id);
    if (en === undefined) return light;
    return { ...light, enabled: en };
  });
}

export function mergeMrP2pDoorSnapshots(
  base: MysteryRoomDoorRuntimeState[],
  snaps: MrP2PDoorSnapshot[],
  opts?: { guestCatchUp?: boolean },
): MysteryRoomDoorRuntimeState[] {
  if (!snaps.length) return base;
  const byId = new Map(snaps.map((s) => [s.id, s]));
  return base.map((d) => {
    const s = byId.get(d.id);
    if (!s) return d;
    // Зачем: гость — локальная анимация двери не затирается устаревшим снимком хоста до прихода нового state.
    if (opts?.guestCatchUp && d.openProgress > s.openProgress + 0.04) {
      return d;
    }
    return { ...d, state: s.state, openProgress: s.openProgress, holdOpenMs: s.holdOpenMs };
  });
}

export function mergeMrP2pTriggerSnapshots(
  base: MysteryRoomTriggerRuntimeState[],
  snaps: MrP2PTriggerSnapshot[],
): MysteryRoomTriggerRuntimeState[] {
  if (!snaps.length) return base;
  const byId = new Map(snaps.map((s) => [s.id, s.lastActivatedAtMs]));
  return base.map((t) => {
    const ts = byId.get(t.id);
    if (ts === undefined) return t;
    return { ...t, lastActivatedAtMs: ts };
  });
}

export function hazardsFromP2pSnapshots(snaps: MrP2PHazardSnapshot[]): MysteryRoomHazardRuntimeState[] {
  return snaps.map((s) => ({
    id: s.id,
    kind: s.kind,
    zoneId: s.zoneId,
    position: { ...s.position },
    radius: s.radius,
    intensity: s.intensity,
  }));
}

export function mergeP2pWorldAlarm(base: MysteryRoomWorldAlarmState, snap: MrP2PWorldAlarmSnapshot): MysteryRoomWorldAlarmState {
  return {
    state: snap.state,
    sourceIncidentId: snap.sourceIncidentId,
    activeUntilMs: snap.activeUntilMs,
  };
}
